import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/utils/tailwind-utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-semibold transition-all duration-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:bg-neutral-70 disabled:text-neutral-0 disabled:cursor-not-allowed disabled:border-neutral-80",
  {
    variants: {
      variant: {
        default:
          "bg-primary-indigo-500 text-white shadow-sm hover:bg-primary-indigo-700 active:bg-primary-indigo-600",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border-2 border-primary-indigo-500 bg-transparent text-primary-indigo-500 shadow-sm hover:bg-primary-indigo-700 hover:text-white hover:border-primary-indigo-700 active:bg-primary-indigo-600 active:border-primary-indigo-600",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        cancel:
          "text-gray-600 bg-transparent hover:bg-gray-100 border border-gray-200",
        // Figma design variant with specific styling
        figma:
          "border border-[#887eb4] bg-[#fbfbfb] text-[#2a2a2a] shadow-[0px_2px_8px_0px_rgba(0,0,0,0.04)] hover:bg-primary-indigo-700 hover:text-white hover:border-primary-indigo-700 active:bg-primary-indigo-600 active:border-primary-indigo-600",
        // Filled variant with primary background color - from Figma design
        filled:
          "bg-primary-indigo-600 text-white shadow-[0px_2px_8px_0px_rgba(0,0,0,0.15)] hover:bg-primary-indigo-700 active:bg-primary-indigo-500 font-medium",
      },
      size: {
        default: "h-10 px-6 py-3",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-12 rounded-md px-10 text-base tracking-wide",
        icon: "h-9 w-9",
        // Figma design size with specific styling
        figma: "h-14 px-7 py-5 rounded-2xl text-[20px] font-medium gap-3",
        // Medium size with specific Figma properties
        md: "w-[156px] h-[44px] rounded-xl py-4 px-12 gap-2 text-base font-medium",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
